import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BadgeInfo, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { goEntitiesApi } from "@/services/go-entities.service";
import { cn } from "@/lib/utils";
import type { GoEntityCardFieldValue, GoFieldProvenancePatch } from "@/types/go-entity-card";

type Props = {
  field: GoEntityCardFieldValue;
  editing?: boolean;
  saving?: boolean;
  onSaveProvenance?: (
    fieldKey: string,
    patch: GoFieldProvenancePatch,
  ) => Promise<{ ok: boolean } | void>;
};

function formatTs(raw?: string | null): string {
  if (!raw) return "—";
  try {
    return new Date(raw).toLocaleString("ru-RU");
  } catch {
    return raw;
  }
}

function hasProvenance(field: GoEntityCardFieldValue): boolean {
  return Boolean(
    field.data_source ||
      field.date_source ||
      field.legitimacy_kls_id ||
      field.legitimacy_label ||
      field.updated_at,
  );
}

/**
 * Бейдж/tooltip provenance в просмотре и popover «Сведения об источнике» в правке.
 */
export function GoFieldProvenanceControl({
  field,
  editing = false,
  saving = false,
  onSaveProvenance,
}: Props) {
  const [open, setOpen] = useState(false);
  const [dataSource, setDataSource] = useState(field.data_source ?? "");
  const [dateSource, setDateSource] = useState(field.date_source ?? "");
  const [legitimacyId, setLegitimacyId] = useState(
    field.legitimacy_kls_id != null ? String(field.legitimacy_kls_id) : "none",
  );

  const legitQuery = useQuery({
    queryKey: ["go-entities", "meta", "legitimacy-options"],
    queryFn: () => goEntitiesApi.listLegitimacyOptions(),
    enabled: editing && open,
    staleTime: 60_000,
  });

  useEffect(() => {
    setDataSource(field.data_source ?? "");
    setDateSource(field.date_source ?? "");
    setLegitimacyId(field.legitimacy_kls_id != null ? String(field.legitimacy_kls_id) : "none");
  }, [field.field_key, field.data_source, field.date_source, field.legitimacy_kls_id]);

  const tipParts = [
    field.legitimacy_label ? `Легитимность: ${field.legitimacy_label}` : null,
    field.data_source ? `Источник: ${field.data_source}` : null,
    field.date_source ? `Дата сведений: ${field.date_source}` : null,
    field.updated_at ? `Обновлено: ${formatTs(field.updated_at)}` : null,
  ].filter(Boolean) as string[];

  const badgeLabel = field.legitimacy_label || (hasProvenance(field) ? "источник" : null);

  if (!editing) {
    if (!badgeLabel) return null;
    return (
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded border border-border bg-muted/40 px-1.5 py-0.5",
              "text-[10px] font-medium text-muted-foreground",
            )}
            title={tipParts.join(" · ")}
          >
            <ShieldCheck className="h-3 w-3" aria-hidden />
            {badgeLabel}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          {tipParts.length ? (
            <ul className="space-y-0.5">
              {tipParts.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          ) : (
            <p>Сведения об источнике не заданы</p>
          )}
        </TooltipContent>
      </Tooltip>
    );
  }

  const save = async () => {
    if (!onSaveProvenance || saving) return;
    const patch: GoFieldProvenancePatch = {
      data_source: dataSource.trim() || null,
      date_source: dateSource.trim() || null,
      legitimacy_kls_id: legitimacyId === "none" ? null : Number(legitimacyId),
    };
    const result = await onSaveProvenance(field.field_key, patch);
    if (!result || result.ok !== false) setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 gap-1 px-2 text-xs text-muted-foreground"
          aria-label="Сведения об источнике значения"
          title="Сведения об источнике"
          disabled={saving}
        >
          <BadgeInfo className="h-3.5 w-3.5" aria-hidden />
          Источник
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[260] w-80 space-y-3" align="end">
        <div>
          <p className="text-sm font-medium text-foreground">Сведения об источнике</p>
          <p className="text-[11px] text-muted-foreground">
            Легитимность, наименование и дата сведений у источника (не системное время записи).
          </p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Легитимность</Label>
          <Select value={legitimacyId} onValueChange={setLegitimacyId} disabled={saving}>
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Не задана" />
            </SelectTrigger>
            <SelectContent className="z-[270]">
              <SelectItem value="none">Не задана</SelectItem>
              {(legitQuery.data ?? []).map((o) => (
                <SelectItem key={o.kls_id} value={String(o.kls_id)}>
                  {o.kls_names}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs" htmlFor={`prov-src-${field.field_key}`}>
            Наименование источника
          </Label>
          <Input
            id={`prov-src-${field.field_key}`}
            className="h-8"
            value={dataSource}
            onChange={(e) => setDataSource(e.target.value)}
            disabled={saving}
            placeholder="Напр. ОК20 / импорт / оператор"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs" htmlFor={`prov-date-${field.field_key}`}>
            Дата сведений (as-of)
          </Label>
          <Input
            id={`prov-date-${field.field_key}`}
            type="date"
            className="h-8"
            value={dateSource ? dateSource.slice(0, 10) : ""}
            onChange={(e) => setDateSource(e.target.value)}
            disabled={saving}
          />
        </div>
        <p className="text-[10px] text-muted-foreground">
          Системно обновлено: {formatTs(field.updated_at)}
        </p>
        <div className="flex justify-end gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
            Отмена
          </Button>
          <Button type="button" size="sm" disabled={saving} onClick={() => void save()}>
            Сохранить
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
